/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useMemo } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type RecommendedApp,
	type ActionTier,
} from '@post-office/message-template--twc-sidenav-banner';

import appTiles from './assets/twc-app-tiles.svg';

type SurfaceProduct = 'jira' | 'confluence';

const styles = cssMap({
	root: {
		width: '100%',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		// Horizontal margins were causing the banner to exceed the side-nav width
		// (width: 100% + horizontal margins overflows the container). Keep only a
		// minimal vertical gap from the surrounding nav items.
		marginInline: 0,
		marginBlock: token('space.050'),
	},
	headerRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.150'),
	},
	logoWrapper: {
		marginTop: token('space.150'),
	},
});

const messages = defineMessages({
	subheadingConfluence: {
		id: 'twc.sidenav.banner.personalized.subheading.confluence',
		defaultMessage: 'Create with Rovo in Confluence',
		description:
			'Personalized banner subheading when the recommended app is Confluence. Shown in the product sidenav.',
	},
	subheadingJira: {
		id: 'twc.sidenav.banner.personalized.subheading.jira',
		defaultMessage: 'Plan your next team project in Jira',
		description:
			'Personalized banner subheading when the recommended app is Jira. Shown in the product sidenav.',
	},
	subheadingLoom: {
		id: 'twc.sidenav.banner.personalized.subheading.loom',
		defaultMessage: 'Setup video meetings with Loom AI',
		description:
			'Personalized banner subheading when the recommended app is Loom. Shown in the product sidenav.',
	},
	appTilesAlt: {
		id: 'twc.sidenav.banner.personalized.app.tiles.alt',
		defaultMessage: 'App tiles illustration',
		description:
			'Alt text for the Teamwork Collection app tiles illustration in the personalized banner.',
	},
	heading: {
		id: 'twc.sidenav.banner.personalized.heading',
		defaultMessage: 'Explore Teamwork Collection',
		description:
			'Main heading for the personalized missing-app activation banner in the product sidenav.',
	},
	ctaButton: {
		id: 'twc.sidenav.banner.personalized.cta',
		defaultMessage: 'Get started',
		description:
			'CTA button text for the personalized missing-app activation banner in the product sidenav.',
	},
	closeLabel: {
		id: 'twc.sidenav.banner.personalized.close',
		defaultMessage: 'Close',
		description:
			'Accessible label for the close button on the personalized missing-app activation banner.',
	},
});

const subheadingByApp: Record<RecommendedApp, (typeof messages)[keyof typeof messages]> = {
	confluence: messages.subheadingConfluence,
	jira: messages.subheadingJira,
	loom: messages.subheadingLoom,
};

const destinationTypeByApp: Record<RecommendedApp, string> = {
	confluence: 'create_with_rovo',
	jira: 'jira_onboarding',
	loom: 'loom_meetings',
};

export type PersonalizedBannerProps = {
	href: string;
	locale?: string;
	recommendedApp: RecommendedApp;
	messageInstanceId?: string;
	onClose?: () => void;
	onCtaClick?: () => void;
	experimentKey?: string;
	actionTier?: ActionTier;
	targetTenantId?: string;
	collectionEntitlementId?: string;
	surfaceProduct?: SurfaceProduct;
};

export const PersonalizedBanner = ({
	href,
	locale,
	recommendedApp,
	messageInstanceId,
	onClose,
	onCtaClick,
	experimentKey,
	actionTier,
	targetTenantId,
	collectionEntitlementId,
	surfaceProduct,
}: PersonalizedBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsAttributes = useMemo(
		() => ({
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
			surfaceProduct,
			destinationType: destinationTypeByApp[recommendedApp],
			arm: 'personalized' as const,
		}),
		[
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
			surfaceProduct,
		],
	);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcPersonalizedBanner',
			attributes: analyticsAttributes,
		}).fire('growth');
	}, [analyticsAttributes, createAnalyticsEvent]);

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerClose',
			attributes: analyticsAttributes,
		}).fire('growth');
		onClose?.();
	};

	const handleCtaClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerCta',
			attributes: analyticsAttributes,
		}).fire('growth');
		onCtaClick?.();
	};

	return (
		<Box xcss={styles.root} testId="twc-personalized-banner">
			<Stack space="space.150">
				<Box xcss={styles.headerRow}>
					<Box xcss={styles.logoWrapper} testId="twc-personalized-banner-logo">
						<Image
							src={appTiles}
							srcDark={appTiles}
							alt={formatMessage(messages.appTilesAlt)}
							testId="twc-personalized-banner-app-tiles"
							width={91}
						/>
					</Box>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={handleClose}
						label={formatMessage(messages.closeLabel)}
						testId="twc-personalized-banner-close"
						appearance="subtle"
					/>
				</Box>
				<Stack space="space.050">
					<Heading as="h3" size="xsmall" testId="twc-personalized-banner-heading">
						{formatMessage(messages.heading)}
					</Heading>
					<Text
						as="p"
						size="small"
						color="color.text.subtle"
						maxLines={2}
						testId="twc-personalized-banner-subheading"
					>
						{formatMessage(subheadingByApp[recommendedApp])}
					</Text>
				</Stack>
				<LinkButton
					shouldFitContainer
					href={href}
					testId="twc-personalized-banner-cta"
					onClick={handleCtaClick}
					target="_blank"
				>
					{formatMessage(messages.ctaButton)}
				</LinkButton>
			</Stack>
		</Box>
	);
};
